import { styled } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { MetaTags, Modal } from "src/components";
import { useUpdateCustomerProfileMutation } from "src/graphql-next/generated/queries";
import {
  useCurrentAccount,
  useDeviceWidth,
  useInvalidateCurrentCustomer,
  useUuid,
} from "src/hooks";
import { useSubscribeEReco } from "src/hooks/useSubscribeEReco";
import { decodeBase64UUID } from "src/utils";

import { ERecoSubscriptionForm } from "./_components/SubscriptionForm";

const StyledModal = styled(Modal)<{ hideCloseButton: boolean }>`
  .MuiDialogContent-root {
    ${({ hideCloseButton }) => hideCloseButton && "padding: 0;"}

    ${(props) => props.theme.breakpoints.down("sm")} {
      padding: 0;
    }
  }
`;

interface Props {
  open: boolean;
  onClose(): void;
}

const EReco = ({ open, onClose }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isMobile } = useDeviceWidth();
  const uuid = useUuid();
  const { customer } = useCurrentAccount();
  const { accountId } = decodeBase64UUID(uuid);
  const invalidateCurrentCustomer = useInvalidateCurrentCustomer();

  const {
    mutate: subscriptionMutation,
    isLoading: subscriptionMutationIsLoading,
  } = useSubscribeEReco();

  const {
    mutate: updateCustomerProfile,
    isLoading: isUpdatingCustomerProfile,
  } = useUpdateCustomerProfileMutation({
    onSuccess: () => {
      onClose();
      void invalidateCurrentCustomer();
      subscriptionMutation({ customerId: customer.id, accountId });
    },
    onError: () => {
      enqueueSnackbar(
        "Une erreur est survenue lors de la sauvegarde de votre adresse email",
        {
          variant: "error",
        },
      );
    },
  });

  const onSubmitEmail = (emailValue: string) =>
    updateCustomerProfile({
      customerId: customer.id,
      payload: {
        email: emailValue,
      },
    });

  return (
    <>
      {open && <MetaTags title="EReco" />}
      <StyledModal
        icon="erecoComputer"
        title="Souscription au E-reco"
        hideCloseButton={isMobile === false}
        disableBackdropClick={subscriptionMutationIsLoading}
        disableEscapeKeyDown={subscriptionMutationIsLoading}
        open={open}
        handleClose={onClose}
        fullWidth
      >
        <ERecoSubscriptionForm
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
          email={customer.email}
          handleClose={onClose}
          onSubmitEmail={onSubmitEmail}
          subscriptionLoading={subscriptionMutationIsLoading}
          updateCustomerLoading={isUpdatingCustomerProfile}
        />
      </StyledModal>
    </>
  );
};

export default EReco;
